/**
 * Maps a saved schedule (or nothing, for a new one) to the schedule form's
 * field values.
 *
 * Pulled out of the dialog's hydrate effect so the two branches sit side by
 * side and can be read at a glance. That matters more than usual here: the
 * edit branch must derive *every* field from the server payload, because any
 * default leaking into it would silently rewrite a saved schedule the next
 * time someone edited an unrelated field.
 */
import type { PipelineSchedule } from '../services/pipelineOrchestrator';
import { parseCronForPicker } from './cronCalendar';
import { NEW_SCHEDULE_DEFAULTS } from './scheduleDefaults';

export interface ScheduleFormValues {
  scheduleName: string;
  tenantId: string;
  scheduleHour: number;
  scheduleMinute: number;
  daysOfWeek: number[];
  customCron: string | null;
  timeZone: string;
  selectedZones: Array<{ zone_slug: string; collectors: string[] }>;
  runMode: 'auto' | 'full' | 'delta';
  vinProcessorType: 'api' | 'selenium';
  skipVinProcessing: boolean;
  enableDedup: boolean;
  notificationEmails: string[];
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
  minIntervalMinutes: number;
  description: string;
}

const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];

/**
 * `enable_dedup` is the current field; `force_reprocess` is its legacy inverse.
 * Older schedules carry only the latter, so both spellings are read.
 */
function readDedup(config: PipelineSchedule['pipeline_config']): boolean {
  if (config.enable_dedup !== undefined) return config.enable_dedup;
  if (config.force_reprocess !== undefined) return !config.force_reprocess;
  return false;
}

/** The wire repeats a zone once per collector; the picker wants one entry per zone. */
function readZones(
  config: PipelineSchedule['pipeline_config']
): ScheduleFormValues['selectedZones'] {
  const grouped = new Map<string, string[]>();
  for (const zone of config.zones ?? []) {
    const collectors = grouped.get(zone.zone_slug) ?? [];
    collectors.push(zone.collector);
    grouped.set(zone.zone_slug, collectors);
  }
  return Array.from(grouped.entries()).map(([zone_slug, collectors]) => ({
    zone_slug,
    collectors
  }));
}

/**
 * Values for a brand-new schedule.
 *
 * `knownRecipients` is the set of addresses already used on existing schedules.
 * Passing it means an address added to one schedule becomes the default for the
 * next, without any stored preference — and it self-corrects when an address
 * stops being used anywhere.
 */
export function blankScheduleForm(
  defaultTenantId?: string,
  knownRecipients?: string[]
): ScheduleFormValues {
  return {
    scheduleName: '',
    tenantId: defaultTenantId ?? '',
    scheduleHour: NEW_SCHEDULE_DEFAULTS.hour,
    scheduleMinute: 0,
    daysOfWeek: [...NEW_SCHEDULE_DEFAULTS.daysOfWeek],
    customCron: null,
    timeZone: NEW_SCHEDULE_DEFAULTS.timeZone,
    selectedZones: [],
    runMode: NEW_SCHEDULE_DEFAULTS.runMode,
    vinProcessorType: 'api',
    skipVinProcessing: false,
    enableDedup: false,
    notificationEmails:
      knownRecipients && knownRecipients.length > 0
        ? [...knownRecipients]
        : [...NEW_SCHEDULE_DEFAULTS.notificationEmails],
    notifyOnSuccess: NEW_SCHEDULE_DEFAULTS.notifyOnSuccess,
    notifyOnFailure: NEW_SCHEDULE_DEFAULTS.notifyOnFailure,
    minIntervalMinutes: NEW_SCHEDULE_DEFAULTS.minIntervalMinutes,
    description: ''
  };
}

/**
 * Values for an existing schedule — every field read from the payload, never
 * from NEW_SCHEDULE_DEFAULTS.
 *
 * A cron the picker can't represent (steps, day-of-month, a month restriction)
 * drops the form into custom mode so the raw expression round-trips unchanged
 * rather than being flattened to whatever the picker could show.
 */
export function scheduleFormValues(schedule: PipelineSchedule): ScheduleFormValues {
  const parsed = parseCronForPicker(schedule.cron_expression);
  const config = schedule.pipeline_config;

  return {
    scheduleName: schedule.schedule_name,
    tenantId: schedule.tenant_id,
    scheduleHour: parsed?.hour ?? NEW_SCHEDULE_DEFAULTS.hour,
    scheduleMinute: parsed?.minute ?? 0,
    daysOfWeek: parsed?.daysOfWeek ?? [...EVERY_DAY],
    customCron: parsed ? null : schedule.cron_expression,
    timeZone: schedule.time_zone,
    selectedZones: readZones(config),
    runMode: (config.run_mode as 'auto' | 'full' | 'delta') || 'auto',
    vinProcessorType: (config.vin_processor_type as 'api' | 'selenium') || 'api',
    skipVinProcessing: config.skip_vin_processing || false,
    enableDedup: readDedup(config),
    notificationEmails: schedule.notification_emails || [],
    notifyOnSuccess: schedule.notify_on_success,
    notifyOnFailure: schedule.notify_on_failure,
    minIntervalMinutes: schedule.min_interval_minutes,
    description: schedule.description || ''
  };
}
